import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CategoriesAPI from '@/api/CategoriesAPI';
import { ICategoryMarketplaceMapping, IMarketplaceCategory } from '@/models/categories.model';
import { StyledRzBadge } from '../CategoryItem/CategoryItemStyles';
import { showErrorToast, showSuccessToast } from '@/components';
import styled from 'styled-components';
import { COLORS, pickThemeFontStyles } from '@/theme';
import { AxiosError } from 'axios';

type Props = {
  categoryId: string;
  mappings: ICategoryMarketplaceMapping[];
  onMappingsChange: (next: ICategoryMarketplaceMapping[]) => void;
  canAdd?: boolean;
};

const MARKETPLACE = 'rozetka';

const StyledBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  padding: 1rem;
  border-radius: 0.375rem;
  border: 1px solid ${COLORS.GREY};
  background-color: ${COLORS.GRAY_LIGHT};
  overflow: visible;
  position: relative;
  z-index: 1;
`;

const StyledLabel = styled.div`
  ${pickThemeFontStyles('14', '20', 'SEMI_BOLD')};
  color: ${COLORS.BLACK};
`;

const StyledEmpty = styled.div`
  ${pickThemeFontStyles('12', '18', 'NORMAL')};
  color: ${COLORS.GRAY_PRIMARY};
`;

const StyledMappingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const StyledMappingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.375rem 0.5rem;
  border-radius: 0.25rem;
  background-color: ${COLORS.WHITE};
  border: 1px solid ${COLORS.GREY};

  & > div {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
`;

const StyledPath = styled.span`
  ${pickThemeFontStyles('12', '16', 'NORMAL')};
  color: ${COLORS.DARK_GRAY};
`;

const StyledMatchedBy = styled.span`
  ${pickThemeFontStyles('10', '14', 'NORMAL')};
  color: ${COLORS.GRAY_PRIMARY};
`;

const StyledRemoveBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
  line-height: 1;
  color: ${COLORS.RED};
  padding: 0 0.25rem;
  flex-shrink: 0;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const StyledSearchWrap = styled.div`
  position: relative;
  width: 100%;
  z-index: 5;
`;

const StyledSearchInput = styled.input`
  width: 100%;
  padding: 0.375rem 0.625rem;
  border: 1px solid ${COLORS.GREY};
  border-radius: 0.25rem;
  ${pickThemeFontStyles('12', '20', 'NORMAL')};
  outline: none;

  &:focus {
    border-color: ${COLORS.YELLOW_PRIMARY};
  }

  &:disabled {
    opacity: 0.5;
  }
`;

const StyledHint = styled.div`
  ${pickThemeFontStyles('12', '16', 'NORMAL')};
  color: ${COLORS.GRAY_PRIMARY};
  margin-top: 0.25rem;
`;

const StyledSuggestionList = styled.ul`
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  right: 0;
  z-index: 20;
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid ${COLORS.GREY};
  border-radius: 0.25rem;
  background: ${COLORS.WHITE};
  max-height: 14rem;
  overflow-y: auto;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.12);
`;

const StyledSuggestionItem = styled.li<{ $isUnavailable: boolean }>`
  padding: 0.375rem 0.75rem;
  ${pickThemeFontStyles('12', '18', 'NORMAL')};
  cursor: ${({ $isUnavailable }) => ($isUnavailable ? 'not-allowed' : 'pointer')};
  color: ${({ $isUnavailable }) => ($isUnavailable ? COLORS.GRAY_PRIMARY : COLORS.BLACK)};
  background-color: ${({ $isUnavailable }) => ($isUnavailable ? COLORS.GRAY_LIGHT : 'transparent')};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;

  &:not(:last-child) {
    border-bottom: 1px solid ${COLORS.GREY};
  }

  &:hover {
    background-color: ${({ $isUnavailable }) => ($isUnavailable ? COLORS.GRAY_LIGHT : COLORS.BRIGHT_GRAY)};
  }
`;

const StyledExtId = styled.span`
  ${pickThemeFontStyles('10', '14', 'NORMAL')};
  color: ${COLORS.GRAY_PRIMARY};
`;

const CategoryMarketplaceMappingsBlock = ({ categoryId, mappings, onMappingsChange, canAdd = true }: Props) => {
  const { t } = useTranslation(['categories']);

  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<IMarketplaceCategory[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const fetchSuggestions = useCallback(async (q: string) => {
    if (!q.trim()) {
      setSuggestions([]);

      return;
    }

    setIsSearching(true);

    try {
      const res = await CategoriesAPI.searchMarketplaceCategories({ marketplace: MARKETPLACE, q, limit: 20 });
      setSuggestions(res.data.data ?? []);
    } catch {
      setSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => fetchSuggestions(query), 300);

    return () => clearTimeout(timer);
  }, [query, fetchSuggestions]);

  const saveMapping = useCallback(
    async (next: ICategoryMarketplaceMapping[]) => {
      const previous = mappings;
      setIsSaving(true);
      onMappingsChange(next);
      setSuggestions([]);
      setQuery('');

      try {
        const res = await CategoriesAPI.updateMarketplaceMappings(categoryId, {
          marketplace: MARKETPLACE,
          external_ids: next.map((m) => m.external_id),
        });
        const saved = res.data?.data;

        if (Array.isArray(saved)) {
          onMappingsChange(
            saved.map((item) => {
              const fallback = next.find((m) => m.external_id === item.external_id);

              return {
                ...item,
                name: item.name || fallback?.name,
                path: item.path || fallback?.path,
              };
            }),
          );
        }

        showSuccessToast({ message: t('category_rozetka_mapping_saved') });
      } catch (error) {
        onMappingsChange(previous);
        const axiosError = error as AxiosError<{ message?: string; errors?: { external_ids?: string[] } }>;
        const takenByOther = Boolean(axiosError.response?.data?.errors?.external_ids?.length);
        const errorMessage = takenByOther
          ? t('category_rozetka_mapped_to_other')
          : axiosError.response?.data?.message || t('category_rozetka_mapping_save_error');
        showErrorToast({ message: errorMessage });
      } finally {
        setIsSaving(false);
      }
    },
    [categoryId, mappings, onMappingsChange, t],
  );

  const handleAdd = (cat: IMarketplaceCategory) => {
    const takenByOther = Boolean(cat.mapped_category_id) && cat.mapped_category_id !== categoryId;

    if (takenByOther) {
      showErrorToast({ message: t('category_rozetka_mapped_to_other') });

      return;
    }

    const alreadyMapped =
      mappings.some((mapping) => mapping.external_id === cat.external_id) || cat.mapped_category_id === categoryId;

    if (alreadyMapped) return;

    const next: ICategoryMarketplaceMapping[] = [
      ...mappings,
      { marketplace: MARKETPLACE, external_id: cat.external_id, name: cat.name, path: cat.path, matched_by: 'manual' },
    ];
    saveMapping(next);
  };

  const handleRemove = (externalId: string) => {
    const next = mappings.filter((m) => m.external_id !== externalId);
    saveMapping(next);
  };

  return (
    <StyledBlock>
      <StyledLabel>{t('category_rozetka_mappings')}</StyledLabel>

      {mappings.length > 0 ? (
        <StyledMappingList>
          {mappings.map((m) => (
            <StyledMappingRow key={m.external_id}>
              <div>
                <StyledRzBadge>{m.external_id}</StyledRzBadge>
                {m.path && <StyledPath>{m.path}</StyledPath>}
                {m.name && !m.path && <StyledPath>{m.name}</StyledPath>}
                <StyledMatchedBy>{m.matched_by}</StyledMatchedBy>
              </div>
              <StyledRemoveBtn
                onClick={() => handleRemove(m.external_id)}
                disabled={isSaving}
                title={t('category_rozetka_remove_mapping')}
              >
                ×
              </StyledRemoveBtn>
            </StyledMappingRow>
          ))}
        </StyledMappingList>
      ) : (
        <StyledEmpty>{t('category_rozetka_no_mappings')}</StyledEmpty>
      )}

      {canAdd && (
        <StyledSearchWrap>
          <StyledSearchInput
            placeholder={t('category_rozetka_search_placeholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={isSaving}
          />

          {isSearching && <StyledHint>{t('category_rozetka_searching')}</StyledHint>}

          {suggestions.length > 0 && (
            <StyledSuggestionList>
              {suggestions.map((cat) => {
                const isAlready =
                  mappings.some((m) => m.external_id === cat.external_id) || cat.mapped_category_id === categoryId;
                const isTakenByOther = Boolean(cat.mapped_category_id) && cat.mapped_category_id !== categoryId;

                return (
                  <StyledSuggestionItem
                    key={cat.external_id}
                    onClick={() => handleAdd(cat)}
                    $isUnavailable={isAlready || isTakenByOther}
                  >
                    <strong>{cat.name}</strong>
                    {cat.path && <span> — {cat.path}</span>}
                    <StyledExtId>#{cat.external_id}</StyledExtId>
                    {isAlready && <StyledMatchedBy>{t('category_rozetka_already_mapped')}</StyledMatchedBy>}
                  </StyledSuggestionItem>
                );
              })}
            </StyledSuggestionList>
          )}
        </StyledSearchWrap>
      )}
    </StyledBlock>
  );
};

export default CategoryMarketplaceMappingsBlock;
